/* @jsx h */
import { defineComponent, ref, h, computed, Ref, onMounted, watch } from "vue";
import { Cart, FoodType, CartItem, Product } from "@lemmonhq/types";
import Sortable from "sortablejs";
import { generateCartItemId } from "@lemmonhq/orders";
import { nanoid } from "nanoid";
import { getFoodTypeIconName } from "@app/utils/clientBusiness";

export type CoursesFinalizerProps = {
  cart: Cart;
  foodTypes: FoodType[];
  products: Product[];
  triggeredFoodTypeIds: Ref<number[]>;
};

// Type to track partitioned items - each unit gets its own entry
type PartitionedItem = CartItem & { partitionId?: string };
const UNASSIGNED_CUSTOMER_ID = "__unassigned__";
const UNASSIGNED_FOOD_TYPE_ID = "__unassigned__";

function findClosestWithAttribute(
  el: HTMLElement | null,
  attribute: string,
): HTMLElement | null {
  while (el) {
    if (el.hasAttribute(attribute)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function setupManualDnD(
  cartItemSelector: string,
  dropZoneSelector: string,
  onDrop: (guid: string, foodTypeId: number) => void,
) {
  const cartItems = document.querySelectorAll<HTMLElement>(cartItemSelector);
  const dropZones = document.querySelectorAll<HTMLElement>(dropZoneSelector);

  let currentlyDragging: HTMLElement | undefined;

  cartItems.forEach((item) => {
    item.setAttribute("draggable", "true");

    item.addEventListener("dragstart", (e) => {
      item.style.opacity = "0.5";
      e.dataTransfer!.effectAllowed = "move";

      const guid = item.getAttribute("data-guid");
      currentlyDragging = item;
      if (guid) {
        e.dataTransfer!.setData("text/plain", guid);
      }
    });

    item.addEventListener("dragend", () => {
      item.style.opacity = "1";
    });
  });

  dropZones.forEach((zone) => {
    zone.addEventListener("dragenter", () => {
      zone.classList.add("drag-over");
    });

    zone.addEventListener("dragleave", () => {
      zone.classList.remove("drag-over");
    });

    zone.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = "move";
    });

    zone.addEventListener("drop", (e) => {
      e.preventDefault();
      //console.log("e", e);
      zone.classList.remove("drag-over");

      const el = currentlyDragging;

      //console.log("el", el, e.target);

      const guid =
        e.dataTransfer!.getData("text/plain") || el?.getAttribute("data-guid");
      const foodTypeIdStr = zone.getAttribute("data-food-type-id");
      //console.log("guid", { guid, foodTypeIdStr });
      if (guid && foodTypeIdStr) {
        onDrop(guid, parseInt(foodTypeIdStr));
      }
      currentlyDragging = undefined;

      //          // Optionally move the element to the drop zone
      //          const draggedEl = document.querySelector<HTMLElement>(
      //            `${cartItemSelector}[data-guid="${guid}"]`
      //          );
      //          if (draggedEl) {
      //            zone.appendChild(draggedEl);
      //          }
    });
  });
}

export const CoursesFinalizer = defineComponent<CoursesFinalizerProps>({
  name: "CoursesSelectComponent",
  props: ["cart", "foodTypes", "products", "triggeredFoodTypeIds"],
  setup(props) {
    const selectedCustomerId = ref<string>(UNASSIGNED_CUSTOMER_ID);
    const customerTabs = computed(() => {
      const seen = new Set<string>();
      const tabs = props.cart.customers
        .filter((customer) => {
          if (seen.has(customer.id)) return false;
          seen.add(customer.id);
          return true;
        })
        .map((customer) => ({
          id: customer.id,
          label: customer.name,
        }));

      return [
        ...tabs,
        {
          id: UNASSIGNED_CUSTOMER_ID,
          label: "Unassigned",
        },
      ];
    });
    const sortedFoodTypes = computed(() =>
      props.foodTypes
        .filter((el) => !el.deleted_at)
        .sort((a, b) => a.order - b.order),
    );
    const allFoodTypeIds = computed(() =>
      sortedFoodTypes.value
        .map((ft) => ft.id.toString())
        .concat(UNASSIGNED_FOOD_TYPE_ID),
    );

    watch(
      customerTabs,
      (tabs) => {
        if (!tabs.some((tab) => tab.id === selectedCustomerId.value)) {
          selectedCustomerId.value = tabs[0]?.id || UNASSIGNED_CUSTOMER_ID;
        }
      },
      { immediate: true },
    );

    const groupedItems = computed(() => {
      const groups: Record<string, Record<string, PartitionedItem[]>> = {};

      for (const customer of customerTabs.value) {
        groups[customer.id] = {};
        for (const foodTypeId of allFoodTypeIds.value) {
          groups[customer.id][foodTypeId] = [];
        }
      }

      for (const item of props.cart.items) {
        const customerId = item.customerId || UNASSIGNED_CUSTOMER_ID;
        const foodTypeId =
          item.foodTypeId?.toString() || UNASSIGNED_FOOD_TYPE_ID;

        if (!groups[customerId]) {
          groups[customerId] = {};
          for (const currentFoodTypeId of allFoodTypeIds.value) {
            groups[customerId][currentFoodTypeId] = [];
          }
        }
        if (!groups[customerId][foodTypeId]) {
          groups[customerId][foodTypeId] = [];
        }

        // Expand items by quantity - each unit becomes a separate draggable item
        const quantity = item.quantity || 1;
        for (let i = 0; i < quantity; i++) {
          // Only add partitionId if quantity > 1 to keep single items simple
          const partitionId = quantity > 1 ? nanoid(6) : undefined;
          groups[customerId][foodTypeId].push({ ...item, partitionId });
        }
      }

      return groups;
    });

    const renderFoodTypeSection = (
      foodType: FoodType | undefined,
      items: PartitionedItem[],
    ) => (
      <div class=" p-1 xl:p-1 border  rounded-lg transition-all select-none shadow shadow-xl">
        <h3 class=" flex items-center gap-2 uppercase text-accent mb-0">
          <span class="material-symbols-outlined">
            {getFoodTypeIconName(foodType?.name)}
          </span>
          {foodType ? foodType.name : "Other"}
        </h3>
        {items.length > 0 ? (
          <div
            data-food-type-id={foodType ? foodType.id : undefined}
            class={
              " space-y-1 p-1 rounded " +
              (foodType ? "food-type-drop-zone" : "")
            }
          >
            {items.map((item) => {
              // Create a unique key and guid for each partitioned item
              const uniqueKey = item.partitionId
                ? `${item.guid}-${item.partitionId}`
                : item.guid;

              return (
                <div
                  key={uniqueKey}
                  data-guid={item.guid}
                  data-partition-id={item.partitionId}
                  class="cart-item p-0 rounded cursor-move hover:bg-base-200 bg-base-100"
                >
                  <div class="flex flex-col justify-between">
                    <div
                      style={`opacity: ${foodType ? 1 : 0.5}`}
                      class="flex items-center gap-2 flex-1"
                    >
                      <span class="bg-accent/90 text-accent-content rounded rounded-1xl  handle material-symbols-outlined text-2xl">
                        drag_indicator
                      </span>
                      <span class="">{item.name}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            data-food-type-id={foodType ? foodType.id : undefined}
            class="py-3 food-type-drop-zone"
          ></div>
        )}
      </div>
    );

    const renderCustomerSection = (
      customerId: string,
      foodTypeGroups: Record<string, PartitionedItem[]>,
    ) => {
      return (
        <div key={customerId} class=" border-b  pt-0">
          <div class=" grid grid-cols-1 gap-4">
            {allFoodTypeIds.value.map((typeId) => {
              const foodType = props.foodTypes.find(
                (ft) => ft.id.toString() === typeId,
              );
              const items = foodTypeGroups[typeId] || [];
              return renderFoodTypeSection(foodType, items);
            })}
          </div>
        </div>
      );
    };

    const renderActiveTab = () => {
      const customerId = selectedCustomerId.value;
      const foodTypeGroups = groupedItems.value[customerId];
      if (!foodTypeGroups) return null;
      return renderCustomerSection(customerId, foodTypeGroups);
    };

    onMounted(() => {
      const first = sortedFoodTypes.value[0];
      if (first) props.triggeredFoodTypeIds.value = [first.id];
    });

    //    onMounted(() => {
    //      setupManualDnD(
    //        ".cart-item",
    //        ".food-type-drop-zone",
    //        (guid, foodTypeId) => {
    //          props.cart.items = props.cart.items.map((item) =>
    //            item.guid === guid ? { ...item, foodTypeId } : item
    //          );
    //        }
    //      );
    //    });
    onMounted(() => {
      const dropZones = document.querySelectorAll(".food-type-drop-zone");

      dropZones.forEach((dropZone) => {
        new Sortable(dropZone as HTMLElement, {
          scroll: true,
          group: "cart-items",
          animation: 150, // ms, animation speed moving items when sorting, `0` — without animation
          easing: "cubic-bezier(1, 0, 0, 1)", // Easing for animation. Defaults to null. See https://easings.net/ for examples.
          ghostClass: "sortable-ghost",
          scrollSensitivity: 100,
          forceAutoScrollFallback: true,
          handle: ".handle", // handle's class
          draggable: ".cart-item", // 🔥 This ensures only items are draggable
          onEnd: (evt) => {
            // Don't do anything if dropped in same zone
            if (evt.from === evt.to) {
              evt.preventDefault();
              return false;
            }

            const itemEl = evt.item as HTMLElement;
            const foodTypeIdStr = (evt.to as HTMLElement).getAttribute(
              "data-food-type-id",
            );
            const foodTypeId = foodTypeIdStr ? parseInt(foodTypeIdStr) : null;

            if (!itemEl || !foodTypeId || isNaN(foodTypeId)) return;

            const guid = itemEl.getAttribute("data-guid");
            const partitionId = itemEl.getAttribute("data-partition-id");
            if (!guid) return;

            // Find the source item
            const sourceItemIndex = props.cart.items.findIndex(
              (item) => item.guid === guid,
            );
            if (sourceItemIndex === -1) return;

            const sourceItem = props.cart.items[sourceItemIndex];

            // Simple case: quantity is 1, just update foodTypeId
            if (sourceItem.quantity === 1) {
              props.cart.items[sourceItemIndex] = {
                ...sourceItem,
                foodTypeId,
              };
            } else {
              // Complex case: split the item
              // 1. Reduce source item quantity by 1
              props.cart.items[sourceItemIndex] = {
                ...sourceItem,
                quantity: sourceItem.quantity - 1,
              };

              // 2. Create new item with quantity 1 and new foodTypeId
              const newPartitionId = nanoid(6);
              const newGuid = generateCartItemId(
                sourceItem.productId,
                sourceItem.selectedBundles,
                sourceItem.isCustom ? parseFloat(sourceItem.price) : undefined,
                sourceItem.customerId,
                newPartitionId,
              );

              const newItem: CartItem = {
                ...sourceItem,
                guid: newGuid,
                quantity: 1,
                foodTypeId,
              };

              props.cart.items.push(newItem);
            }

            // Remove the dragged element from DOM (will re-render from reactive data)
            evt.item.remove();
          },
        });
      });
    });
    const hiddenClass =
      import.meta.env.VITE_ENV === "production" ? "hidden" : "";

    return () => (
      <div class="grid h-full min-h-0 flex-1 grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden border-t bg-base-200">
        <div class="bg-base-300 flex gap-2 px-2 py-2  border-b  overflow-x-auto shrink-0">
          {sortedFoodTypes.value.map((foodType, i) => {
            const isActive = props.triggeredFoodTypeIds.value.includes(
              foodType.id,
            );
            return (
              <button
                key={i}
                onClick={(e) => {
                  const currentValue = props.triggeredFoodTypeIds.value;
                  if (currentValue.includes(foodType.id)) {
                    props.triggeredFoodTypeIds.value = currentValue.filter(
                      (id) => id !== foodType.id,
                    );
                  } else {
                    props.triggeredFoodTypeIds.value.push(foodType.id);
                  }
                }}
                class={
                  "btn btn-sm " + (isActive ? "btn-primary" : "btn-neutral")
                }
              >
                {foodType.name}
              </button>
            );
          })}
        </div>
        <div
          class={`px-2 xl:px-4 flex justify-between overflow-x-auto gap-2 shrink-0 py-2 border-b bg-base-100 ${hiddenClass}`}
        >
          {customerTabs.value.map((customer) => {
            return (
              <a
                key={customer.id}
                role="tab"
                onClick={() => {
                  selectedCustomerId.value = customer.id;
                }}
                class={
                  "cursor-pointer flex-1 flex justify-center rounded rounded-lg btn btn-xs " +
                  (props.cart.customers.length > 0 ? "" : " hidden ") +
                  (selectedCustomerId.value === customer.id
                    ? " btn-accent  "
                    : " btn-neutral bg-base-300 ")
                }
              >
                <span class=" flex items-center gap-2 uppercase whitespace-nowrap">
                  <span class="material-symbols-outlined">person</span>
                  {customer.label}
                </span>
              </a>
            );
          })}
        </div>
        <div class="scroll-container min-h-0 overflow-y-scroll overscroll-contain px-2 py-2 touch-pan-y xl:px-4">
          {renderActiveTab()}
        </div>
      </div>
    );
  },
});
